import { get, makeRequestDeduped, post } from '../../../../infra/rest';
import { ChatFilterSlaStatus, ChatStatuses, GetChatRealtimeCountStatusResponse } from '../typings';
import {
  GetChatSearch,
  GetChatStatusFilterCounts,
  GetChatStatusFilterCount,
} from '../components/chat-list-section/chat-row/typings';
import requestConfig from '../../../../shared/config/request';
import { CustomFieldCondition } from '../components/chat-list-section/custom-field-filter/typings';
import { serializeConditionsForWire } from '../components/chat-list-section/custom-field-filter/utils';

interface GetChatSearchArgs {
  onlyMsgSearch?: boolean;
  tags?: string[];
  waba?: string[];
  limit: number;
  search?: string;
  status?: ChatStatuses;
  slaStatus?: ChatFilterSlaStatus;
  assignedUserId?: string;
  messageExists?: boolean;
  chatId?: string;
  nextChatId?: string;
  nextChatTimestamp?: number;
  nextMessageId?: string;
  nextMessageScore?: number;
  customList?: {
    [listId: string]: string;
  };
  nextMessageTimestamp?: number;
  signal?: AbortSignal;
  tagIds?: string[];
  filterAIAwaitingResponse?: boolean;
  chatType?: 'WA_GROUP';
  excludeConversationChannels?: boolean;
  customFieldConditions?: CustomFieldCondition[];
}

export const getChatSearch = async (args: GetChatSearchArgs): Promise<GetChatSearch> => {
  const queryParams = new URLSearchParams();

  if (!!args.tags && args.tags?.length > 0) {
    args.tags.forEach((tag) => {
      if (typeof tag === 'string' && tag.length > 0) {
        queryParams.append('tags_v2', tag);
      }
    });
  }
  if (!!args.tagIds && args.tagIds?.length > 0) {
    args.tagIds.forEach((tagId) => {
      if (typeof tagId === 'string' && tagId.length > 0) {
        queryParams.append('tag_ids', tagId);
      }
    });
  }
  if (!!args.waba && args.waba?.length > 0) {
    args.waba.forEach((waba) => {
      if (typeof waba === 'string' && waba.length > 0) {
        queryParams.append('waba', waba);
      }
    });
  }
  if (typeof args.limit === 'number') {
    queryParams.set('limit', args.limit.toString());
  }
  if (typeof args.search === 'string' && args.search.length > 0) {
    queryParams.set('searchTerm', args.search);
  }
  if (typeof args.status === 'string' && args.status.length > 0) {
    queryParams.set('status', args.status);
  }
  if (typeof args.slaStatus === 'string' && args.slaStatus.length > 0) {
    queryParams.set('slaStatus', args.slaStatus);
  }
  if (typeof args.assignedUserId === 'string' && args.assignedUserId.length > 0) {
    queryParams.set('assignedUserId', args.assignedUserId);
  }
  if (typeof args.messageExists === 'boolean') {
    queryParams.set('messageExists', args.messageExists.toString());
  }
  if (typeof args.chatId === 'string' && args.chatId.length > 0) {
    queryParams.set('chatId', args.chatId);
  }
  if (typeof args.nextChatId === 'string' && args.nextChatId.length > 0) {
    queryParams.set('nextChatId', args.nextChatId);
  }
  if (typeof args.nextChatTimestamp === 'number') {
    queryParams.set('nextChatTimestamp', args.nextChatTimestamp.toString());
  }
  if (typeof args.nextMessageId === 'string' && args.nextMessageId.length > 0) {
    queryParams.set('nextMessageId', args.nextMessageId);
  }
  if (typeof args.nextMessageScore === 'number') {
    queryParams.set('nextMessageScore', args.nextMessageScore.toString());
  }
  if (typeof args.nextMessageTimestamp === 'number') {
    queryParams.set('nextMessageTimestamp', args.nextMessageTimestamp.toString());
  }
  if (typeof args.filterAIAwaitingResponse === 'boolean') {
    queryParams.set('filterAIAwaitingResponse', args.filterAIAwaitingResponse.toString());
  }
  if (typeof args.chatType === 'string' && args.chatType.length > 0) {
    queryParams.set('chatType', args.chatType);
  }
  if (typeof args.excludeConversationChannels !== 'boolean' || !args.excludeConversationChannels) {
    queryParams.set('includeConversationChannels', 'true');
  }
  if (args.customList && Object.entries(args.customList).length > 0) {
    const customList: { id: string; value: string }[] = [];
    Object.entries(args.customList).forEach(([listKey, listValue]) => {
      if (listKey && listValue) {
        customList.push({ id: listKey, value: listValue });
      }
    });
    if (customList.length > 0) {
      queryParams.set('customFields_v2', JSON.stringify(customList));
    }
  }

  const completeConditions = serializeConditionsForWire(args.customFieldConditions ?? []);
  if (completeConditions.length > 0) {
    queryParams.set('customFields_v3', JSON.stringify(completeConditions));
  }

  queryParams.set('getMultiselectCustomFields', 'true');

  const fullUrl = args.onlyMsgSearch
    ? `${requestConfig.aiBotBaseUrl}/v1/chat/search`
    : `${requestConfig.baseUrl}/v3/search`;
  return get<GetChatSearch>(
    `${fullUrl}${queryParams.toString().length > 0 ? `?${queryParams.toString()}` : ''}`,
    {},
    true,
    false,
    true,
    false,
    false,
    args.signal
  );
};

export const getChatStatusFilterCounts = async (): Promise<GetChatStatusFilterCounts> => {
  return get<GetChatStatusFilterCounts>('/v1/search/filters', {}, true);
  // return Promise.resolve<GetChatStatusFilterCounts>({
  //   filterCounts: [
  //     {
  //       type: 'ALL_CHATS',
  //       label: 'All chats',
  //       counts: [
  //         { type: ChatStatuses.ALL, label: 'All', count: undefined },
  //         { type: ChatStatuses.UNREAD, label: 'Unread', count: undefined },
  //         { type: ChatStatuses.WAITING_FOR_RESPONSE, label: 'Awaiting reply', count: undefined },
  //         { type: ChatStatuses.DONE, label: 'Resolved', count: undefined },
  //         { type: ChatStatuses.CLOSED, label: 'Expired', count: undefined },
  //       ],
  //     },
  //     {
  //       type: 'MY_CHATS',
  //       label: 'My chats',
  //       counts: [
  //         { type: ChatStatuses.ALL, label: 'All', count: undefined },
  //         { type: ChatStatuses.UNREAD, label: 'Unread', count: undefined },
  //         { type: ChatStatuses.WAITING_FOR_RESPONSE, label: 'Awaiting reply', count: undefined },
  //         { type: ChatStatuses.DONE, label: 'Resolved', count: undefined },
  //         { type: ChatStatuses.CLOSED, label: 'Expired', count: undefined },
  //       ],
  //     },
  //     {
  //       type: 'UNASSIGNED',
  //       label: 'Unassigned',
  //       counts: [
  //         { type: ChatStatuses.ALL, label: 'All', count: undefined },
  //         { type: ChatStatuses.UNREAD, label: 'Unread', count: undefined },
  //         { type: ChatStatuses.WAITING_FOR_RESPONSE, label: 'Awaiting reply', count: undefined },
  //         { type: ChatStatuses.DONE, label: 'Resolved', count: undefined },
  //         { type: ChatStatuses.CLOSED, label: 'Expired', count: undefined },
  //       ],
  //      },
  //     {
  //       type: 'SLA_STATUS',
  //       label: 'SLA breached',
  //       counts: [
  //         { type: ChatStatuses.ALL, label: 'All', count: undefined },
  //         { type: ChatStatuses.UNREAD, label: 'Unread', count: undefined },
  //         { type: ChatStatuses.CLOSED, label: 'Expired', count: undefined },
  //       ],
  //     }
  //   ],
  // });
};

interface GetChatStatusFilterCountsArgs {
  tags?: string[];
  waba?: string[];
  assignedUserId?: string;
  customList?: {
    [listId: string]: string;
  };
  tagIds?: string[];
  searchTerm?: string;
  filterAIAwaitingResponse?: boolean;
  chatType?: 'WA_GROUP';
  customFieldConditions?: CustomFieldCondition[];
}

export const getChatStatusFilterCountsLatest = async (
  args: GetChatStatusFilterCountsArgs
): Promise<GetChatStatusFilterCounts> => {
  let bodyData: any = {};
  if (!!args.tags && args.tags?.length > 0) {
    bodyData['tags_v2'] = args.tags;
  }
  if (!!args.waba && args.waba?.length > 0) {
    bodyData['integrationIds'] = args.waba;
  }
  if (typeof args.assignedUserId === 'string' && args.assignedUserId.length > 0) {
    bodyData['assignedUserId'] = args.assignedUserId;
  }
  const queryParams = new URLSearchParams();
  if (typeof args.filterAIAwaitingResponse === 'boolean') {
    queryParams.set('filterAIAwaitingResponse', args.filterAIAwaitingResponse.toString());
    bodyData['filterAIAwaitingResponse'] = args.filterAIAwaitingResponse.toString();
  }
  if (args.customList && Object.entries(args.customList).length > 0) {
    const customList: { id: string; value: string }[] = [];
    Object.entries(args.customList).forEach(([listKey, listValue]) => {
      if (listKey && listValue) {
        customList.push({ id: listKey, value: listValue });
      }
    });
    if (customList.length > 0) {
      bodyData['customFields_v2'] = customList;
    }
  }
  const completeConditions = serializeConditionsForWire(args.customFieldConditions ?? []);
  if (completeConditions.length > 0) {
    bodyData['customFields_v3'] = completeConditions;
  }
  if (typeof args.searchTerm === 'string' && args.searchTerm.trim().length > 0) {
    bodyData['searchTerm'] = args.searchTerm.trim();
  }
  if (typeof args.chatType === 'string' && args.chatType.length > 0) {
    bodyData['chatType'] = args.chatType;
  }
  bodyData['includeConversationChannels'] = true;
  return makeRequestDeduped<GetChatStatusFilterCounts>(
    `/v1/search/filters/count`,
    {
      body: JSON.stringify(bodyData),
      method: 'POST',
    },
    true
  );
};

export const getChatRealtimeCountStatus = async (): Promise<GetChatRealtimeCountStatusResponse> => {
  return get<GetChatRealtimeCountStatusResponse>('/v1/search/realtime-count-status', {}, true);
};

interface RdsChatCountEnabledOrgsResponse {
  orgIds: string[];
}

export const getRdsChatCountEnabledOrgs = async (): Promise<RdsChatCountEnabledOrgsResponse> => {
  return makeRequestDeduped<RdsChatCountEnabledOrgsResponse>(
    `${requestConfig.aiBotBaseUrl}/v1/internal/rds-chat-count-enabled-orgs`,
    {
      headers: {
        Authorization: 'ba18f307-7601-497b-a953-406fd48873c0',
      },
    },
    false,
    false,
    true
  );
};

interface GetChatCountsResponse {
  filterCounts: Array<{
    type: string;
    label: string;
    count?: number;
    counts: Array<{
      type: string;
      label: string;
      count: number;
    }>;
  }>;
}

export const getChatCounts = async (args: GetChatStatusFilterCountsArgs): Promise<GetChatStatusFilterCounts> => {
  let bodyData: any = {};
  if (!!args.tagIds && args.tagIds?.length > 0) {
    bodyData['tagIds'] = args.tagIds;
  }
  if (!!args.waba && args.waba?.length > 0) {
    bodyData['integrationIds'] = args.waba;
  }
  if (typeof args.assignedUserId === 'string' && args.assignedUserId.length > 0) {
    bodyData['assignedUserId'] = args.assignedUserId;
  }
  if (args.customList && Object.entries(args.customList).length > 0) {
    const customFields: { id: string; value: string }[] = [];
    Object.entries(args.customList).forEach(([listKey, listValue]) => {
      if (listKey && listValue) {
        customFields.push({ id: listKey, value: listValue });
      }
    });
    if (customFields.length > 0) {
      bodyData['customFields'] = customFields;
    }
  }
  if (typeof args.searchTerm === 'string' && args.searchTerm.length > 0) {
    bodyData['searchTerm'] = args.searchTerm;
  }
  if (typeof args.chatType === 'string' && args.chatType.length > 0) {
    bodyData['chatType'] = args.chatType;
  }

  const response = await post<GetChatCountsResponse>(
    `${requestConfig.aiBotBaseUrl}/v1/chat/count`,
    {
      body: JSON.stringify(bodyData),
    },
    true,
    false,
    true
  );

  // Transform response format to match GetChatStatusFilterCount structure
  const statusTypeMap: Record<string, ChatStatuses> = {
    unresolved: ChatStatuses.UNRESOLVED,
    done: ChatStatuses.DONE,
    waiting_for_response: ChatStatuses.WAITING_FOR_RESPONSE,
    unread: ChatStatuses.UNREAD,
    all: ChatStatuses.ALL,
    open: ChatStatuses.OPEN,
    closed: ChatStatuses.CLOSED,
    read: ChatStatuses.READ,
  };

  const transformedFilterCounts: GetChatStatusFilterCount[] = (response.filterCounts || []).map((filterCount) => {
    const transformedCounts: Array<{ type: ChatStatuses; label: string; count?: string }> = [];

    (filterCount.counts || []).forEach((countItem) => {
      const statusType = statusTypeMap[countItem.type.toLowerCase()];
      if (statusType) {
        transformedCounts.push({
          type: statusType,
          label: countItem.label,
          count: typeof countItem.count === 'number' ? countItem.count.toString() : undefined,
        });
      }
    });

    return {
      type: filterCount.type as GetChatStatusFilterCount['type'],
      label: filterCount.label,
      counts: transformedCounts,
    };
  });

  return {
    filterCounts: transformedFilterCounts,
  };
};
